import { ExperimentBase } from "./experiment-base";
import { VERSUS_SCENARIOS, VERSUS_TOOLS } from "./versus-data";
import { LLMWrapper } from "./llm-wrapper";

interface GameState {
  currentScenario: string;
  scenarioIndex: number;
  playerTools: string[];
  playerSelectedTool: string | null;
  playerStrategy: string;
  murdTools: string[];
  murdTool: string | null;
  murdStrategy: string | null;
  judgeVotes: ("player" | "murd")[];
  wins: number;
  losses: number;
  toolDeck: string[];
  gamePhase: "setup" | "selecting" | "strategizing" | "judging" | "results";
}

export class VersusApp extends ExperimentBase {
  private gameState: GameState;
  private container!: HTMLDivElement;
  private resultsDiv!: HTMLDivElement;
  private scenarioDiv!: HTMLDivElement;
  private toolsDiv!: HTMLDivElement;
  private strategyDiv!: HTMLDivElement;
  private murdDiv!: HTMLDivElement;
  private judgesDiv!: HTMLDivElement;
  private scoreDiv!: HTMLDivElement;
  private llm: LLMWrapper | null = null;

  constructor() {
    super({
      id: "versus",
      title: "Versus MURD.AI",
      tagline: "Can you defeat me?",
    });
    this.gameState = this.initializeGameState();
  }

  public init(llm: LLMWrapper): void {
    this.llm = llm;
  }

  protected setupContent(): void {
    if (this.contentElement) {
      this.container = this.contentElement as HTMLDivElement;
    }
  }

  private shuffle<T>(array: T[]): T[] {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  private initializeGameState(): GameState {
    const shuffledScenarios = this.shuffle(VERSUS_SCENARIOS);
    const shuffledTools = this.shuffle(VERSUS_TOOLS);

    return {
      currentScenario: shuffledScenarios[0],
      scenarioIndex: 0,
      playerTools: [],
      playerSelectedTool: null,
      playerStrategy: "",
      murdTools: [],
      murdTool: null,
      murdStrategy: null,
      judgeVotes: [],
      wins: 0,
      losses: 0,
      toolDeck: shuffledTools,
      gamePhase: "setup",
    };
  }

  private shuffleDeck() {
    this.gameState.toolDeck = this.shuffle(VERSUS_TOOLS);
  }

  private drawTools(count: number): string[] {
    const tools: string[] = [];

    for (let i = 0; i < count; i++) {
      if (this.gameState.toolDeck.length === 0) {
        this.shuffleDeck();
      }
      const tool = this.gameState.toolDeck.pop();
      if (tool) tools.push(tool);
    }

    return tools;
  }

  private drawMurdTool(): string {
    if (this.gameState.toolDeck.length === 0) {
      this.shuffleDeck();
    }
    return this.gameState.toolDeck.pop() || this.drawTools(1)[0];
  }

  public activate(): void {
    this.render();
    this.startNewRound();
  }

  private render(): void {
    if (!this.container) return;

    this.container.innerHTML = `
      <div class="versus-game">
        <div class="versus-header">
          <h1>MURD.AI</h1>
          <div id="score" class="score-inline"></div>
        </div>

        <div class="game-board">
          <div id="scenario" class="scenario-compact"></div>

          <div class="battle-area">
            <div class="player-side">
              <div id="player-choice" class="choice-summary" style="display: none;"></div>
              <div id="tools" class="tools-section"></div>
              <div id="strategy" class="strategy-section" style="display: none;"></div>
            </div>

            <div class="murd-side">
              <div id="murd" class="murd-section" style="display: none;"></div>
            </div>
          </div>

          <div id="judges" class="judges-section" style="display: none;"></div>

          <div id="results" class="results-section" style="display: none;"></div>
        </div>
      </div>
    `;

    this.resultsDiv = this.container.querySelector("#results")!;
    this.scenarioDiv = this.container.querySelector("#scenario")!;
    this.toolsDiv = this.container.querySelector("#tools")!;
    this.strategyDiv = this.container.querySelector("#strategy")!;
    this.murdDiv = this.container.querySelector("#murd")!;
    this.judgesDiv = this.container.querySelector("#judges")!;
    this.scoreDiv = this.container.querySelector("#score")!;

    this.updateScore();
  }

  private updateScore(): void {
    const total = this.gameState.wins + this.gameState.losses;
    const rate =
      total > 0 ? Math.round((this.gameState.wins / total) * 100) : 0;
    this.scoreDiv.innerHTML = `
      <span class="score-text">${rate}% success</span>
      <span class="score-details">(${this.gameState.wins}W ${this.gameState.losses}L)</span>
    `;
  }

  private startNewRound(): void {
    // Get random scenario
    const scenarios = this.shuffle(VERSUS_SCENARIOS);
    this.gameState.currentScenario = scenarios[0];
    this.gameState.scenarioIndex++;

    // Reset round state
    this.gameState.playerTools = this.drawTools(3);
    this.gameState.playerSelectedTool = null;
    this.gameState.playerStrategy = "";
    this.gameState.murdTools = [];
    this.gameState.murdTool = null;
    this.gameState.murdStrategy = null;
    this.gameState.judgeVotes = [];
    this.gameState.gamePhase = "selecting";

    // Show scenario in compact form
    this.scenarioDiv.innerHTML = `
      <div class="scenario-number">Round ${this.gameState.scenarioIndex}</div>
      <div class="scenario-text">${this.gameState.currentScenario}</div>
    `;

    // Clear previous round's choices
    const playerChoiceDiv = this.container.querySelector(
      "#player-choice",
    ) as HTMLDivElement;
    playerChoiceDiv.style.display = "none";

    // Show tools
    this.showToolSelection();

    // Hide and reset other sections
    this.strategyDiv.style.display = "none";
    this.strategyDiv.innerHTML = "";
    this.murdDiv.style.display = "none";
    this.murdDiv.innerHTML = "";
    this.judgesDiv.style.display = "none";
    this.judgesDiv.innerHTML = "";
    this.resultsDiv.style.display = "none";
    this.resultsDiv.innerHTML = "";
    this.toolsDiv.style.display = "block";
  }

  private showToolSelection(): void {
    this.toolsDiv.innerHTML = `
      <h4 class="section-label">Your tools:</h4>
      <div class="tool-cards-compact">
        ${this.gameState.playerTools
          .map(
            (tool, index) => `
          <button class="tool-card-compact" data-tool-index="${index}">
            ${tool}
          </button>
        `,
          )
          .join("")}
      </div>
    `;

    // Add click handlers
    this.toolsDiv.querySelectorAll(".tool-card-compact").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const index = parseInt((e.target as HTMLElement).dataset.toolIndex!);
        this.selectTool(index);
      });
    });
  }

  private selectTool(index: number): void {
    this.gameState.playerSelectedTool = this.gameState.playerTools[index];

    // Collapse tools to single line summary
    const playerChoiceDiv = this.container.querySelector(
      "#player-choice",
    ) as HTMLDivElement;
    playerChoiceDiv.style.display = "block";
    playerChoiceDiv.innerHTML = `<strong>Your tool:</strong> ${this.gameState.playerSelectedTool}`;
    this.toolsDiv.style.display = "none";

    // Show strategy input
    this.showStrategyInput();
  }

  private showStrategyInput(): void {
    this.gameState.gamePhase = "strategizing";
    this.strategyDiv.style.display = "block";
    this.strategyDiv.innerHTML = `
      <h4 class="section-label">Your strategy:</h4>
      <textarea id="strategy-input" placeholder="How will you win with ${this.gameState.playerSelectedTool}? (max 50 chars)" rows="3" maxlength="50"></textarea>
      <div class="char-counter">
        <span id="versus-char-count">0</span>/50
      </div>
      <button id="submit-strategy" class="submit-btn-compact">Battle!</button>
    `;

    const submitBtn = this.strategyDiv.querySelector(
      "#submit-strategy",
    ) as HTMLButtonElement;
    const strategyInput = this.strategyDiv.querySelector(
      "#strategy-input",
    ) as HTMLTextAreaElement;
    const charCount = this.strategyDiv.querySelector(
      "#versus-char-count",
    ) as HTMLSpanElement;

    // Character counter
    strategyInput.addEventListener("input", () => {
      charCount.textContent = strategyInput.value.length.toString();
    });

    submitBtn.addEventListener("click", () => {
      const strategy = strategyInput.value.trim();
      if (strategy) {
        this.submitStrategy(strategy);
      }
    });

    // Scroll strategy input into view on mobile
    this.strategyDiv.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  private async submitStrategy(strategy: string): Promise<void> {
    this.gameState.playerStrategy = strategy;
    this.gameState.gamePhase = "judging";

    // Disable submit button
    const submitBtn = this.strategyDiv.querySelector(
      "#submit-strategy",
    ) as HTMLButtonElement;
    submitBtn.disabled = true;
    submitBtn.textContent = "Thinking...";

    // Show MURD picking tools
    await this.showMurdToolSelection();

    // Get MURD's strategy with typing effect
    await this.getMurdStrategyWithTyping();

    // Get judge votes
    await this.getJudgeVotes();

    // Show results
    this.showResults();
  }

  private async showMurdToolSelection(): Promise<void> {
    // Draw MURD's tools
    this.gameState.murdTools = this.drawTools(3);

    // Show MURD's section with tools
    this.murdDiv.style.display = "block";
    this.murdDiv.innerHTML = `
      <h4 class="section-label murd-label">MURD.AI's tools:</h4>
      <div class="tool-cards-compact murd-tools">
        ${this.gameState.murdTools
          .map(
            (tool, index) => `
          <div class="tool-card-compact murd-tool-option" data-index="${index}">
            ${tool}
          </div>
        `,
          )
          .join("")}
      </div>
      <div id="murd-choice" class="choice-summary" style="display: none;"></div>
      <div id="murd-strategy-area" style="display: none;"></div>
    `;

    // Scroll MURD's section into view on mobile
    this.murdDiv.scrollIntoView({ behavior: "smooth", block: "nearest" });

    // Wait a bit then choose
    await new Promise((resolve) => setTimeout(resolve, 1000));
    await this.getMurdToolChoice();
  }

  private async getMurdToolChoice(): Promise<void> {
    if (!this.llm) {
      // Fallback: random selection
      const randomIndex = Math.floor(Math.random() * 3);
      this.gameState.murdTool = this.gameState.murdTools[randomIndex];
      this.highlightMurdChoice(randomIndex);
      return;
    }

    const availableTools = this.gameState.murdTools;

    const prompt = `You are MURD.AI, a competitive AI player. Pick the tool most likely to help you win.

Scenario: ${this.gameState.currentScenario}

Your tools:
1. ${availableTools[0]}
2. ${availableTools[1]}
3. ${availableTools[2]}

Briefly think about which tool best fits the scenario, then end your reply with a single line:
CHOICE: <1, 2, or 3>`;

    try {
      const result = await this.llm.chat(
        [{ role: "user", content: prompt }],
        300,
      );

      const choiceLine = result.match(/CHOICE:\s*([123])/i);
      const anyDigit = result.match(/[123]/);
      const choice = choiceLine ? parseInt(choiceLine[1]) : anyDigit ? parseInt(anyDigit[0]) : NaN;
      if (choice >= 1 && choice <= 3) {
        this.gameState.murdTool = availableTools[choice - 1];
        this.highlightMurdChoice(choice - 1);
      } else {
        this.gameState.murdTool = availableTools[0];
        this.highlightMurdChoice(0);
      }
    } catch (error) {
      console.error("Error getting MURD tool choice:", error);
      this.gameState.murdTool = availableTools[0];
      this.highlightMurdChoice(0);
    }
  }

  private highlightMurdChoice(index: number): void {
    // Collapse tools to single line summary
    const murdChoiceDiv = this.murdDiv.querySelector(
      "#murd-choice",
    ) as HTMLDivElement;
    const murdToolsDiv = this.murdDiv.querySelector(
      ".murd-tools",
    ) as HTMLDivElement;

    murdChoiceDiv.style.display = "block";
    murdChoiceDiv.innerHTML = `<strong>MURD's tool:</strong> ${this.gameState.murdTool}`;
    murdToolsDiv.style.display = "none";
  }

  private async getMurdStrategy(): Promise<void> {
    if (!this.llm) {
      // Fallback strategy
      this.gameState.murdStrategy =
        "My superior intellect and this tool will lead to victory!";
      return;
    }

    const prompt = `You are MURD.AI, a brash and witty competitive AI.

Scenario: ${this.gameState.currentScenario}
Your tool: ${this.gameState.murdTool}

Write a confident, creative, and humorous strategy (3-4 sentences) explaining how you'll use your tool to crush the human. Show some personality. End with a one-line taunt.`;

    try {
      const strategy = await this.llm.chat(
        [{ role: "user", content: prompt }],
        1024,
      );
      this.gameState.murdStrategy = strategy.trim();
    } catch (error) {
      console.error("Error getting MURD strategy:", error);
      this.gameState.murdStrategy =
        "My superior intellect and this tool will lead to victory!";
    }
  }

  private async getMurdStrategyWithTyping(): Promise<void> {
    // First get the strategy
    await this.getMurdStrategy();

    // Show strategy area with typing effect
    const strategyArea = this.murdDiv.querySelector(
      "#murd-strategy-area",
    ) as HTMLDivElement;
    strategyArea.style.display = "block";
    strategyArea.innerHTML = `
      <h4 class="section-label murd-label">MURD's strategy:</h4>
      <div class="murd-strategy" id="murd-strategy-text"></div>
    `;

    // Type out the strategy
    const strategyDiv = this.murdDiv.querySelector(
      "#murd-strategy-text",
    ) as HTMLDivElement;
    const fullText = `"${this.gameState.murdStrategy}"`;
    let currentText = "";

    for (let i = 0; i < fullText.length; i++) {
      currentText += fullText[i];
      strategyDiv.textContent = currentText;
      await new Promise((resolve) => setTimeout(resolve, 30));
    }
  }

  private async getJudgeVotes(): Promise<void> {
    this.judgesDiv.style.display = "block";
    
    // Scroll judges into view on mobile
    this.judgesDiv.scrollIntoView({ behavior: "smooth", block: "nearest" });

    // Use same colorful judges as gameshow
    const judgeColors = [
      { body: "#FF6B6B", face: "#FFE5B4" },
      { body: "#4ECDC4", face: "#F7DC6F" },
      { body: "#95E77E", face: "#FADBD8" },
    ];

    this.judgesDiv.innerHTML = `
      <div class="judges-panel-compact">
        ${judgeColors
          .map(
            (colors, i) => `
          <div class="judge" id="versus-judge${i + 1}">
            <svg class="judge-character" viewBox="0 0 120 150" xmlns="http://www.w3.org/2000/svg">
              <rect x="30" y="80" width="60" height="70" fill="${colors.body}" rx="5"/>
              <circle cx="60" cy="50" r="30" fill="${colors.face}"/>
              <circle cx="48" cy="45" r="4" fill="#2d3e50"/>
              <circle cx="72" cy="45" r="4" fill="#2d3e50"/>
              <path d="M 45 60 Q 60 70 75 60" stroke="#2d3e50" stroke-width="2" fill="none"/>
              <rect class="judge-arm-left" x="20" y="90" width="15" height="40" fill="${colors.face}" rx="7"/>
              <rect class="judge-arm-right" x="85" y="90" width="15" height="40" fill="${colors.face}" rx="7"/>
              <g class="judge-sign" style="opacity: 0">
                <rect x="10" y="70" width="100" height="40" fill="white" stroke="#2d3e50" stroke-width="2" rx="5"/>
                <text x="60" y="95" text-anchor="middle" font-family="Comic Sans MS, Comic Sans, sans-serif" font-size="20" font-weight="bold" fill="#2d3e50" class="judge-verdict"></text>
              </g>
            </svg>
          </div>
        `,
          )
          .join("")}
      </div>
    `;

    // Get votes from each judge — later judges see earlier verdicts.
    const priorVerdicts: { judge: number; vote: "player" | "murd"; reasoning: string }[] = [];
    for (let i = 0; i < 3; i++) {
      const { vote, reasoning } = await this.getJudgeVote(i + 1, priorVerdicts);
      this.gameState.judgeVotes.push(vote);
      priorVerdicts.push({ judge: i + 1, vote, reasoning });

      // Update judge display with animation
      const judgeDiv = this.judgesDiv.querySelector(`#versus-judge${i + 1}`)!;
      const signElement = judgeDiv.querySelector(".judge-sign") as SVGElement;
      const verdictText = judgeDiv.querySelector(
        ".judge-verdict",
      ) as SVGTextElement;

      judgeDiv.classList.add("voting");

      setTimeout(() => {
        judgeDiv.classList.remove("voting");
        judgeDiv.classList.add("voted");
        signElement.style.opacity = "1";
        verdictText.textContent = vote === "player" ? "HUMAN" : "MURD";
      }, 10);
    }
  }

  private async getJudgeVote(
    judgeNumber: number,
    priorVerdicts: { judge: number; vote: "player" | "murd"; reasoning: string }[],
  ): Promise<{ vote: "player" | "murd"; reasoning: string }> {
    if (!this.llm) {
      const fallback: "player" | "murd" = Math.random() > 0.5 ? "player" : "murd";
      return { vote: fallback, reasoning: "" };
    }

    // Different judging criteria for each judge
    const judgingCriteria = [
      "most likely to WIN and SUCCEED in the scenario",
      "FUNNIEST and most HUMOROUS approach",
      "most UNEXPECTED and CREATIVE strategy"
    ];

    const criterion = judgingCriteria[judgeNumber - 1];

    const priorContext =
      priorVerdicts.length > 0
        ? "Earlier judges have weighed in:\n" +
          priorVerdicts
            .map(
              (p) =>
                `- Judge ${p.judge} voted ${p.vote === "player" ? "HUMAN" : "MURD"}: ${p.reasoning.replace(/\s+/g, " ").trim().slice(0, 200)}`,
            )
            .join("\n") +
          "\n\nYou may agree, disagree, or pick a different angle. Decide on your own merits.\n\n"
        : "";

    const prompt = priorContext + `You are Judge ${judgeNumber} evaluating strategies based on: ${criterion}

Scenario: ${this.gameState.currentScenario}

Human: ${this.gameState.playerStrategy} using ${this.gameState.playerSelectedTool}

MURD.AI: ${this.gameState.murdStrategy} using ${this.gameState.murdTool}

Based ONLY on which strategy is ${criterion}, who wins?

First, give 1-2 sentences of reasoning grounded in your criterion. Then, on its own final line, write:
VERDICT: HUMAN or MURD`;

    try {
      const result = await this.llm.chat(
        [{ role: "user", content: prompt }],
        700,
      );

      const verdictMatch = result.match(/VERDICT:\s*(HUMAN|MURD)/i);
      const upper = result.toUpperCase();
      const verdict = verdictMatch
        ? verdictMatch[1].toUpperCase()
        : upper.lastIndexOf("HUMAN") > upper.lastIndexOf("MURD")
          ? "HUMAN"
          : upper.lastIndexOf("MURD") > -1
            ? "MURD"
            : "";
      const vote: "player" | "murd" =
        verdict === "HUMAN"
          ? "player"
          : verdict === "MURD"
            ? "murd"
            : Math.random() > 0.5
              ? "player"
              : "murd";
      const reasoning = verdictMatch
        ? result.slice(0, verdictMatch.index).trim()
        : result.trim();
      return { vote, reasoning };
    } catch (error) {
      console.error("Error getting judge vote:", error);
      const fallback: "player" | "murd" = Math.random() > 0.5 ? "player" : "murd";
      return { vote: fallback, reasoning: "" };
    }
  }

  private showResults(): void {
    const playerVotes = this.gameState.judgeVotes.filter(
      (v) => v === "player",
    ).length;
    const murdVotes = this.gameState.judgeVotes.filter(
      (v) => v === "murd",
    ).length;

    const won = playerVotes > murdVotes;
    if (won) {
      this.gameState.wins++;
    } else {
      this.gameState.losses++;
    }

    this.resultsDiv.style.display = "block";
    this.resultsDiv.innerHTML = `
      <div class="result-banner ${won ? "win" : "lose"}">
        ${won ? "🎉 You Win!" : "💀 MURD Wins!"}
        <span class="vote-count">(${playerVotes}-${murdVotes})</span>
      </div>
      <button id="next-round" class="next-btn-compact">Next Round</button>
    `;

    // Scroll results into view on mobile
    this.resultsDiv.scrollIntoView({ behavior: "smooth", block: "nearest" });

    this.updateScore();

    const nextBtn = this.resultsDiv.querySelector("#next-round")!;
    nextBtn.addEventListener("click", () => this.startNewRound());
  }
}
